import React, {
    FC,
    HTMLAttributes,
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useId,
    useRef,
} from 'react';

import { useRouter } from 'next/router';

import clsx from 'clsx';
import styles from './modal.module.css';
import { Typography, TypographyProps } from '~/shared/components';
import Close from '$icons/close.svg';
import { useScrollLock } from '~/shared/hooks';

export type ModalVariants =
    | 'drawerRight'
    | 'drawerLeft'
    | 'modal'
    | 'compact'
    | 'full'
    | 'lightBox';

export type ModalProps = HTMLAttributes<HTMLDialogElement> & {
    isOpen: boolean;
    onClose: () => void;
    headline?: ReactNode;
    headLineVariant?: TypographyProps<'div'>['variant'];
    footer?: ReactNode;
    onOpenCallback?: () => void;
    onCloseCallback?: () => void;
    variant?: ModalVariants;
    scrollLock?: boolean;
    disableClickOutsideToClose?: boolean;
    removeCloseButton?: boolean;
    removeBodyPadding?: boolean;
    doNotRemoveContentFromDOMWhenClosed?: boolean;
    removeBorders?: boolean;
    darkHeader?: boolean;
    doNotCloseOnRouteChange?: boolean;
    // trackingName?: string;
};

const Modal: FC<ModalProps> = memo((props) => {
    const {
        isOpen,
        onClose,
        headline,
        headLineVariant = 'display5',
        footer,
        children,
        onCloseCallback,
        onOpenCallback,
        variant = 'modal',
        scrollLock,
        disableClickOutsideToClose,
        removeCloseButton,
        // trackingName,
        removeBodyPadding,
        doNotRemoveContentFromDOMWhenClosed,
        removeBorders,
        darkHeader,
        className,
        doNotCloseOnRouteChange,
        ...rest
    } = props;

    // Use useRef to persist the dialog ref across renders
    const modalRef = useRef<HTMLDialogElement | null>(null);

    const openIsTracked = useRef(false);
    const id = useId();
    const { lock, unlock } = useScrollLock();
    const router = useRouter();

    const handleOpen = useCallback(() => {
        // if (trackingName && !openIsTracked.current) {
        //     // trackModalOpen(trackingName);
        //     openIsTracked.current = true;
        // }

        onOpenCallback?.();

        if (scrollLock) {
            lock(id);
        }

        const dialog = modalRef.current;
        if (dialog && !dialog.open) {
            dialog.showModal();
        }
    }, [scrollLock, id, lock, onOpenCallback]);

    const handleClose = useCallback(() => {
        onCloseCallback?.();
        if (scrollLock) {
            unlock(id);
        }
        openIsTracked.current = false;
        const dialog = modalRef.current;
        if (dialog && dialog.open) {
            dialog.close();
        }
    }, [scrollLock, id, onCloseCallback, unlock]);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (e.target === modalRef.current) {
                onClose();
            }
        },
        [onClose]
    );

    useEffect(() => {
        if (isOpen) {
            handleOpen();
        } else {
            handleClose();
        }
        // Only run when isOpen changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        const dialog = modalRef.current;
        if (!dialog) return;

        const closeListener = () => onClose();
        dialog.addEventListener('close', closeListener);
        if (!disableClickOutsideToClose) {
            dialog.addEventListener('click', handleOutsideClick);
        }

        return () => {
            dialog.removeEventListener('close', closeListener);
            dialog.removeEventListener('click', handleOutsideClick);
        };
    }, [disableClickOutsideToClose, handleOutsideClick, onClose]);

    // Close modal on route change start
    useEffect(() => {
        if (!isOpen || doNotCloseOnRouteChange) return;
        const handleRouteChange = () => {
            onClose();
        };
        router.events?.on('routeChangeStart', handleRouteChange);
        return () => {
            router.events?.off('routeChangeStart', handleRouteChange);
        };
    }, [isOpen, onClose, router, doNotCloseOnRouteChange]);

    const removeContentWhenClosed = !isOpen && !doNotRemoveContentFromDOMWhenClosed;

    return (
        <dialog
            ref={modalRef}
            className={clsx(
                styles[`${variant}Root`],
                { [styles.removeBorders]: removeBorders },
                className
            )}
            {...rest}
        >
            {!removeContentWhenClosed && (
                <div
                    className={clsx(styles.content, {
                        [styles.removeBodyPadding]: removeBodyPadding,
                    })}
                    data-modal-content
                >
                    {variant !== 'lightBox' && (
                        <div
                            data-modal-header
                            className={clsx({ [styles.darkHeader]: darkHeader })}
                        >
                            <Typography
                                data-modal-title
                                variant={headLineVariant}
                                as="div"
                                isDarkBackground={darkHeader}
                            >
                                {headline}
                            </Typography>
                            {!removeCloseButton && (
                                <button
                                    data-modal-close
                                    className={clsx(styles.closeBtn, {
                                        [styles.darkHeader]: darkHeader,
                                    })}
                                    aria-label="Close modal"
                                    onClick={onClose}
                                >
                                    <Close />
                                </button>
                            )}
                        </div>
                    )}
                    <div data-modal-main>{children}</div>
                    {variant === 'lightBox' && (
                        <button className={styles.closeBtnModal} onClick={onClose}>
                            <Close />
                        </button>
                    )}
                    {footer && variant !== 'lightBox' && (
                        <div
                            data-modal-footer
                            className={clsx({ [styles.removeBorders]: removeBorders })}
                        >
                            {footer}
                        </div>
                    )}
                </div>
            )}
        </dialog>
    );
});

Modal.displayName = 'Modal';

export { Modal };
